import { zodResolver } from '@hookform/resolvers/zod';
import { Grid } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { InputField } from '../../../../../../components/atoms/input-field';

import { useApplyDiscountForParkingTicketValidationSchema } from './schema/apply-discount-for-parking-ticket-validation-schema';

import { Dialog, DialogActions } from '@/components/molecules/dialog';
import { useSettleParkingTicket } from '@/features/visits/hooks/mutation/use-settle-parking-ticket';

interface FormValues {
  ticketNumber: number;
}

const defaultValues: FormValues = {
  ticketNumber: 0,
};

interface Props extends DialogActions {
  visitorId: number | null;
  isPayDisabled: boolean;
}

export const ApplyDiscountForParkingTicketDialog = ({
  open,
  visitorId,
  isPayDisabled,
  onCancel,
}: Props) => {
  const { t } = useTranslation(['visit', 'validation']);

  const validationSchema = useApplyDiscountForParkingTicketValidationSchema();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, isDirty, isSubmitting },
  } = useForm({
    defaultValues,
    resolver: zodResolver(validationSchema),
    mode: 'onChange',
  });

  const { mutate: settleParkingTicket, isPending } = useSettleParkingTicket(() => {
    toast.success(t('visit:modal.success'));
    reset(defaultValues);
    onCancel?.();
  });

  const onSubmit = (data: FormValues) => {
    if (visitorId === null || isPayDisabled) return;

    settleParkingTicket({ visitorId, ticketNumber: data.ticketNumber });
  };

  const handleCancel = () => {
    reset(defaultValues);
    onCancel?.();
  };

  return (
    <Dialog
      onCancel={handleCancel}
      open={open}
      title={t('visit:modal.apply')}
      confirmButtonText={t('visit:modal.pay')}
      isConfirmButtonDisabled={
        !isValid || !isDirty || isPayDisabled || visitorId === null || isPending
      }
      isConfirmButtonSubmitting={isSubmitting || isPending}
      onConfirm={handleSubmit(onSubmit)}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Controller
            control={control}
            name="ticketNumber"
            render={({ field, fieldState: { error } }) => (
              <InputField
                {...field}
                error={!!error?.message}
                fullWidth
                helperText={error?.message}
                label={t('visit:modal.ticket')}
                type="text"
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                onChange={event => {
                  const ticketNumber = event.target.value;

                  if (/^\d*$/.test(ticketNumber)) {
                    field.onChange(ticketNumber === '' ? Number.NaN : Number(ticketNumber));
                  }
                }}
                required
                value={field.value || ''}
              />
            )}
          />
        </Grid>
      </Grid>
    </Dialog>
  );
};
